/* eslint-disable no-param-reassign */
import React, {
    useContext, useCallback, useEffect
} from 'react';
import _ from 'lodash';
import { wizardProps } from '@xengage/gw-portals-wizard-react';
import { useModal } from '@jutro/components';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { EntityUtil } from '@xengage/gw-portals-util-js';
import { screenReaderAnnounce, focusNextElement } from 'premier-capability-hooks';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import AddlIntMsg from "premier-capability-policyjob-react/components/AdditionalInterestComponent/AdditionalInterestComponent.messages.js";
import { PremierConstants } from 'premier-constant-service';
import presentationMetadata from './AddlInterestTextBoxComponent.metadata.json5';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function AddlInterestTextBoxComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { showConfirm } = useModal();
    const {
        wizardData, updateWizardData, addlIntPath, isLineLevel
    } = props;
    const viewModelService = useContext(ViewModelServiceContext);
    const {
        onValidate,
    } = useValidation('AddlInterestTextBoxComponent');

    const metadata = presentationMetadata.addlInterestTextBox;
    const initAddlnInt = _.get(
        wizardData,
        `${addlIntPath}.value`
    );

    useEffect(() => {
        _.get(wizardData, addlIntPath)?.children?.forEach((item) => {
            item.isValidationReq.value = true;
        });
    }, [addlIntPath, wizardData]);

    const initializeAddlInterest = (addlInterestVM) => {
        const arrFieValue = _.get(addlInterestVM.value, `arrFie`) !== undefined
            ? _.get(addlInterestVM.value, `arrFie`) : false;
        const covTypFieValue = _.get(addlInterestVM.value, `covTypFie`) !== undefined
            ? _.get(addlInterestVM.value, `covTypFie`) : 'LiaCov';

        _.set(addlInterestVM, `arrFie.value`, arrFieValue);
        _.set(addlInterestVM, `covTypFie.value`, covTypFieValue);
    }

    const addAdditionalInterest = useCallback(() => {
        const addlInterestLcl = _.get(wizardData, addlIntPath);

        addlInterestLcl.value = addlInterestLcl.value || [];
        screenReaderAnnounce('Additional fields have been populated');

        const { _xCenter, _dtoName } = addlInterestLcl;
        const addlInterestVM = viewModelService.create({
            tempID: EntityUtil.nextId(),
            isValidationReq: true
        }, _xCenter, _dtoName);

        initializeAddlInterest(addlInterestVM);

        addlInterestLcl.pushElement(addlInterestVM);

        const newwizardData = viewModelService.clone(wizardData);

        _.set(wizardData, 'wizardData', newwizardData);
        updateWizardData(wizardData);
    }, [wizardData, addlIntPath, viewModelService, updateWizardData]);

    const onModelChange = (updatedVM) => {
        const newwizardData = viewModelService.clone(updatedVM);

        _.set(wizardData, 'wizardData', newwizardData);
        updateWizardData(wizardData);
    };

    const removeAdditionalInterest = useCallback(
        (evt) => {

            const addlInterestListPath = `${addlIntPath}.value`;
            const addlInterestPath = evt.path;
            const addlInterest = _.get(wizardData, addlInterestPath).value;
            const addlInterestList = _.get(wizardData, addlInterestListPath);

            showConfirm({
                title: AddlIntMsg.removeAdditionalInterest,
                message: AddlIntMsg.removeAddlnIntMsg,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.yes,
                cancelButtonText: commonMessages.no
            }).then(
                (results) => {
                    if (results === 'cancel') {
                        return _.noop();
                    }

                    const newriskAnalysisList = _.remove(addlInterestList,
                        (riskAnalysisFromList) => _.isEqual(riskAnalysisFromList, addlInterest));

                    _.set(wizardData, newriskAnalysisList, addlInterestListPath);

                    const newwizardData = viewModelService.clone(wizardData);

                    _.set(wizardData, 'wizardData', newwizardData);
                    updateWizardData(wizardData);
                    screenReaderAnnounce(`Additional Interest ${evt.index + 1} deleted`);
                    focusNextElement(true, 0, 'addAddlInterestBtn');

                    return true;
                }, _.noop
            );
        },
        [addlIntPath, wizardData, showConfirm, viewModelService, updateWizardData]
    );

    const getDefaultVaueForDesofIntFie = useCallback((isLineLevelP) => {
        if(isLineLevelP) {
            if(wizardData.baseData.preferredlanguage_Ext.value.code === PremierConstants.preferredFrenchCode) {
                return 'Aucun'
            }
 
            return 'None'            
        }
        
        return _.get(wizardData, 'value.lobData.spoAndRecLine.coverables.locRisks[0].accountLocFie.displayName')
    },[wizardData])

    const overrideAddlIntProps = useCallback(() => {
        if (initAddlnInt !== undefined) {
            const overrideProps = initAddlnInt.map((value, index) => {
                value.isValidationReq = true;

                return {
                    [`covTypFie${index}`]: {
                        visible: !_.isUndefined(isLineLevel) && isLineLevel,
                        defaultValue: 'LiaCov'
                    },
                    [`wriNotNumOfDay${index}`]: {
                        visible: !_.isUndefined(isLineLevel) && isLineLevel
                    },
                    [`desofIntFie${index}`]: {
                        defaultValue: getDefaultVaueForDesofIntFie(isLineLevel)
                    },
                    [`arrAmoFie${index}`]: {
                        readOnly: !value.arrFie
                    }
                };
            });

            return Object.assign({}, ...overrideProps);
        }

        return null;
    }, [getDefaultVaueForDesofIntFie, initAddlnInt, isLineLevel]);

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showRequired: true
        },
        addlInsuredContainer: {
            path: `${addlIntPath}.children`
        },
        ...overrideAddlIntProps()
    };

    const resolvers = {
        resolveCallbackMap: {
            addAdditionalInterest,
            removeAdditionalInterest
        }
    };

    return (
        <ViewModelForm
            uiProps={metadata}
            model={wizardData}
            overrideProps={overrideProps}
            onModelChange={onModelChange}
            onValidationChange={onValidate}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

AddlInterestTextBoxComponent.propTypes = wizardProps;
export default AddlInterestTextBoxComponent;
